import React from 'react';

import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR.ts';

type RenderCallback = () => React.ReactNode;

const pending: RenderCallback[] = [];
function queueRenderCallback(callback: RenderCallback) {
	pending.push(callback);
}

let inject = queueRenderCallback;

/**
 * Bootstrap script that is rendered inline before any data streams are emitted
 *
 * This initializes the following:
 * * __s - A map of stream names to a list of emitted values
 * * __sL - A map of stream names to a set of listeners
 * * __sR - A function that initializes a stream if it doesn't exist
 * * __sE - A function that emits a value to a stream
 * * __sC - A function that inserts an emotion style tag into the head. Self-deleting to avoid hydration mismatches.
 */
/* istanbul ignore next */
const streamBootstrapFn = (window) => {
	if (!window.__s) {
		window.__s = {};
	}
	if (!window.__sL) {
		window.__sL = {};
	}
	window.__sR = (s) => {
		if (!window.__s[s]) {
			window.__s[s] = [];
		}
		if (!window.__sL[s]) {
			window.__sL[s] = new Set();
		}
	};
	window.__sE = (s, v) => {
		try {
			if (!window.__s[s]) {
				window.__s[s] = [];
			}
			window.__s[s].push(v);
			if (window.__sL[s]) {
				window.__sL[s].forEach((fn) => fn(v));
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('[__sE] error', e);
		}
	};
};

export function getStreamInject() {
	return inject;
}

/**
 * Set the function to use for injecting content into the SSR stream
 * @param callback - The callback to set the inject function to
 *
 * This will immediately emit any pending callbacks
 */
export function setStreamInject(callback: (cb: RenderCallback) => void) {
	inject = callback;

	if (pending.length > 0) {
		pending.forEach(inject);
		pending.length = 0;
	}
}

export function clearStreamInject() {
	inject = queueRenderCallback;
}

const streams = new Map<string, EventStream<any>>();
/**
 * Clear all streams
 */
export function clearServerStreams() {
	streams.clear();
}

class EventStream<T extends object> {
	private initialized = false;
	private stream: string;

	constructor(stream: string) {
		this.stream = stream;
	}

	emit(value: T) {
		inject(() => {
			let script = '';
			if (!this.initialized) {
				script = `__sR("${this.stream}");`;
				this.initialized = true;
			}

			script += `__sE("${this.stream}", ${stringifyForSSR(value)});`;

			return React.createElement('script', {
				dangerouslySetInnerHTML: {
					__html: script,
				},
			});
		});
	}
}

export function getServerStream<T extends object>(key: string): EventStream<T> {
	if (streams.size === 0) {
		inject(() => {
			return React.createElement('script', {
				dangerouslySetInnerHTML: {
					__html: `(${streamBootstrapFn.toString()})(window)`,
				},
			});
		});
	}

	let stream = streams.get(key);
	if (!stream) {
		stream = new EventStream(key);
		streams.set(key, stream);
	}
	return stream;
}
